/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {isDevMode} from '../application_ref';
import {DebugElement, DebugNode, EventListener, getDebugNode, indexDebugNode, removeDebugNodeFromIndex} from '../debug/debug_node';
import {Injector} from '../di';
import {ErrorHandler} from '../error_handler';
import {NgModuleRef} from '../linker/ng_module_factory';
import {Renderer2, RendererFactory2, RendererStyleFlags2, RendererType2} from '../render/api';
import {Sanitizer} from '../security';
import {Type} from '../type';

import {isViewDebugError, viewDestroyedError, viewWrappedDebugError} from './errors';
import {resolveDep} from './provider';
import {dirtyParentQueries, getQueryValue} from './query';
import {createInjector, createNgModuleRef} from './refs';
import {ArgumentType, BindingFlags, CheckType, DebugContext, DepDef, ElementData, NgModuleDefinition, NgModuleProviderDef, NodeDef, NodeFlags, NodeLogger, ProviderOverride, RootData, Services, ViewData, ViewDefinition, ViewState, asElementData, asPureExpressionData} from './types';
import {NOOP, isComponentView, renderNode, splitDepsDsl, viewParentEl} from './util';
import {checkAndUpdateNode, checkAndUpdateView, checkNoChangesNode, checkNoChangesView, createComponentView, createEmbeddedView, createRootView, destroyView} from './view';


let /** @type {?} */ initialized = false;
/**
 * @return {?}
 */
export function initServicesIfNeeded() {
  if (initialized) {
    return;
  }
  initialized = true;
  const /** @type {?} */ services = isDevMode() ? createDebugServices() : createProdServices();
  Services.setCurrentNode = services.setCurrentNode;
  Services.createRootView = services.createRootView;
  Services.createEmbeddedView = services.createEmbeddedView;
  Services.createComponentView = services.createComponentView;
  Services.createNgModuleRef = services.createNgModuleRef;
  Services.overrideProvider = services.overrideProvider;
  Services.clearProviderOverrides = services.clearProviderOverrides;
  Services.checkAndUpdateView = services.checkAndUpdateView;
  Services.checkNoChangesView = services.checkNoChangesView;
  Services.destroyView = services.destroyView;
  Services.resolveDep = resolveDep;
  Services.createDebugContext = services.createDebugContext;
  Services.handleEvent = services.handleEvent;
  Services.updateDirectives = services.updateDirectives;
  Services.updateRenderer = services.updateRenderer;
  Services.dirtyParentQueries = dirtyParentQueries;
}
/**
 * @return {?}
 */
function createProdServices() {
  return {
    setCurrentNode: () => {},
    createRootView: createProdRootView,
    createEmbeddedView: createEmbeddedView,
    createComponentView: createComponentView,
    createNgModuleRef: createNgModuleRef,
    overrideProvider: NOOP,
    clearProviderOverrides: NOOP,
    checkAndUpdateView: checkAndUpdateView,
    checkNoChangesView: checkNoChangesView,
    destroyView: destroyView,
    createDebugContext: (view: ViewData, nodeIndex: number) => new DebugContext_(view, nodeIndex),
    handleEvent: (view: ViewData, nodeIndex: number, eventName: string, event: any) =>
                     view.def.handleEvent(view, nodeIndex, eventName, event),
    updateDirectives: (view: ViewData, checkType: CheckType) => view.def.updateDirectives(
                          checkType === CheckType.CheckAndUpdate ? prodCheckAndUpdateNode :
                                                                   prodCheckNoChangesNode,
                          view),
    updateRenderer: (view: ViewData, checkType: CheckType) => view.def.updateRenderer(
                        checkType === CheckType.CheckAndUpdate ? prodCheckAndUpdateNode :
                                                                 prodCheckNoChangesNode,
                        view),
  };
}
/**
 * @return {?}
 */
function createDebugServices() {
  return {
    setCurrentNode: debugSetCurrentNode,
    createRootView: debugCreateRootView,
    createEmbeddedView: debugCreateEmbeddedView,
    createComponentView: debugCreateComponentView,
    createNgModuleRef: debugCreateNgModuleRef,
    overrideProvider: debugOverrideProvider,
    clearProviderOverrides: debugClearProviderOverrides,
    checkAndUpdateView: debugCheckAndUpdateView,
    checkNoChangesView: debugCheckNoChangesView,
    destroyView: debugDestroyView,
    createDebugContext: (view: ViewData, nodeIndex: number) => new DebugContext_(view, nodeIndex),
    handleEvent: debugHandleEvent,
    updateDirectives: debugUpdateDirectives,
    updateRenderer: debugUpdateRenderer,
  };
}
/**
 * @param {?} elInjector
 * @param {?} projectableNodes
 * @param {?} rootSelectorOrNode
 * @param {?} def
 * @param {?} ngModule
 * @param {?=} context
 * @return {?}
 */
function createProdRootView(
    elInjector: Injector, projectableNodes: any[][], rootSelectorOrNode: string | any,
    def: ViewDefinition, ngModule: NgModuleRef<any>, context?: any): ViewData {
  const /** @type {?} */ rendererFactory: RendererFactory2 = ngModule.injector.get(RendererFactory2);
  return createRootView(
      createRootData(elInjector, ngModule, rendererFactory, projectableNodes, rootSelectorOrNode),
      def, context);
}
/**
 * @param {?} elInjector
 * @param {?} projectableNodes
 * @param {?} rootSelectorOrNode
 * @param {?} def
 * @param {?} ngModule
 * @param {?=} context
 * @return {?}
 */
function debugCreateRootView(
    elInjector: Injector, projectableNodes: any[][], rootSelectorOrNode: string | any,
    def: ViewDefinition, ngModule: NgModuleRef<any>, context?: any): ViewData {
  const /** @type {?} */ rendererFactory: RendererFactory2 = ngModule.injector.get(RendererFactory2);
  const /** @type {?} */ root = createRootData(
      elInjector, ngModule, new DebugRendererFactory2(rendererFactory), projectableNodes,
      rootSelectorOrNode);
  const /** @type {?} */ defWithOverride = applyProviderOverridesToView(def);
  return callWithDebugContext(
      DebugAction.create, createRootView, null, [root, defWithOverride, context]);
}
/**
 * @param {?} elInjector
 * @param {?} ngModule
 * @param {?} rendererFactory
 * @param {?} projectableNodes
 * @param {?} rootSelectorOrNode
 * @return {?}
 */
function createRootData(
    elInjector: Injector, ngModule: NgModuleRef<any>, rendererFactory: RendererFactory2,
    projectableNodes: any[][], rootSelectorOrNode: any): RootData {
  const /** @type {?} */ sanitizer = ngModule.injector.get(Sanitizer);
  const /** @type {?} */ errorHandler = ngModule.injector.get(ErrorHandler);
  const /** @type {?} */ renderer = rendererFactory.createRenderer(null, null);
  return {
    ngModule,
    injector: elInjector, projectableNodes,
    selectorOrNode: rootSelectorOrNode, sanitizer, rendererFactory, renderer, errorHandler
  };
}
/**
 * @param {?} parentView
 * @param {?} anchorDef
 * @param {?} viewDef
 * @param {?=} context
 * @return {?}
 */
function debugCreateEmbeddedView(
    parentView: ViewData, anchorDef: NodeDef, viewDef: ViewDefinition, context?: any): ViewData {
  const /** @type {?} */ defWithOverride = applyProviderOverridesToView(viewDef);
  return callWithDebugContext(
      DebugAction.create, createEmbeddedView, null,
      [parentView, anchorDef, defWithOverride, context]);
}
/**
 * @param {?} parentView
 * @param {?} nodeDef
 * @param {?} viewDef
 * @param {?} hostElement
 * @return {?}
 */
function debugCreateComponentView(
    parentView: ViewData, nodeDef: NodeDef, viewDef: ViewDefinition, hostElement: any): ViewData {
  const /** @type {?} */ defWithOverride = applyProviderOverridesToView(viewDef);
  return callWithDebugContext(
      DebugAction.create, createComponentView, null,
      [parentView, nodeDef, defWithOverride, hostElement]);
}
/**
 * @param {?} moduleType
 * @param {?} parentInjector
 * @param {?} bootstrapComponents
 * @param {?} def
 * @return {?}
 */
function debugCreateNgModuleRef(
    moduleType: Type<any>, parentInjector: Injector, bootstrapComponents: Type<any>[],
    def: NgModuleDefinition): NgModuleRef<any> {
  const /** @type {?} */ defWithOverride = applyProviderOverridesToNgModule(def);
  return createNgModuleRef(moduleType, parentInjector, bootstrapComponents, defWithOverride);
}

const /** @type {?} */ providerOverrides = new Map<any, ProviderOverride>();
/**
 * @param {?} override
 * @return {?}
 */
function debugOverrideProvider(override: ProviderOverride) {
  providerOverrides.set(override.token, override);
}
/**
 * @return {?}
 */
function debugClearProviderOverrides() {
  providerOverrides.clear();
}
/**
 * @param {?} def
 * @return {?}
 */
function applyProviderOverridesToView(def: ViewDefinition): ViewDefinition {
  if (providerOverrides.size === 0) {
    return def;
  }
  const /** @type {?} */ elementIndicesWithOverwrittenProviders = findElementIndicesWithOverwrittenProviders(def);
  if (elementIndicesWithOverwrittenProviders.length === 0) {
    return def;
  }
  // clone the whole view definition,
  // as it maintains references between the nodes that are hard to update.
  def = /** @type {?} */(( def.factory))(() => NOOP);
  for (let /** @type {?} */ i = 0; i < elementIndicesWithOverwrittenProviders.length; i++) {
    applyProviderOverridesToElement(def, elementIndicesWithOverwrittenProviders[i]);
  }
  return def;
/**
 * @param {?} def
 * @return {?}
 */
function findElementIndicesWithOverwrittenProviders(def: ViewDefinition): number[] {
    const /** @type {?} */ elIndicesWithOverwrittenProviders: number[] = [];
    let /** @type {?} */ lastElementDef: NodeDef|null = null;
    for (let /** @type {?} */ i = 0; i < def.nodes.length; i++) {
      const /** @type {?} */ nodeDef = def.nodes[i];
      if (nodeDef.flags & NodeFlags.TypeElement) {
        lastElementDef = nodeDef;
      }
      if (lastElementDef && nodeDef.flags & NodeFlags.CatProviderNoDirective &&
          providerOverrides.has( /** @type {?} */((nodeDef.provider)).token)) {
        elIndicesWithOverwrittenProviders.push( /** @type {?} */((lastElementDef)).index);
        lastElementDef = null;
      }
    }
    return elIndicesWithOverwrittenProviders;
  }
/**
 * @param {?} viewDef
 * @param {?} elIndex
 * @return {?}
 */
function applyProviderOverridesToElement(viewDef: ViewDefinition, elIndex: number) {
    for (let /** @type {?} */ i = elIndex + 1; i < viewDef.nodes.length; i++) {
      const /** @type {?} */ nodeDef = viewDef.nodes[i];
      if (nodeDef.flags & NodeFlags.TypeElement) {
        // stop at the next element
        return;
      }
      if (nodeDef.flags & NodeFlags.CatProviderNoDirective) {
        // Make all providers lazy, so that we don't get into trouble
        // with ordering problems of providers on the same element
        nodeDef.flags |= NodeFlags.LazyProvider;
        const /** @type {?} */ provider = /** @type {?} */(( nodeDef.provider));
        const /** @type {?} */ override = providerOverrides.get(provider.token);
        if (override) {
          nodeDef.flags = (nodeDef.flags & ~NodeFlags.CatProviderNoDirective) | override.flags;
          provider.deps = splitDepsDsl(override.deps);
          provider.value = override.value;
        }
      }
    }
  }
}
/**
 * @param {?} def
 * @return {?}
 */
function applyProviderOverridesToNgModule(def: NgModuleDefinition): NgModuleDefinition {
  if (providerOverrides.size === 0 || !hasOverrrides(def)) {
    return def;
  }
  // clone the whole view definition,
  // as it maintains references between the nodes that are hard to update.
  def = /** @type {?} */(( def.factory))(() => NOOP);
  applyProviderOverrides(def);
  return def;
/**
 * @param {?} def
 * @return {?}
 */
function hasOverrrides(def: NgModuleDefinition): boolean {
    return def.providers.some(
        node =>
            !!(node.flags & NodeFlags.CatProviderNoDirective) && providerOverrides.has(node.token));
  }
/**
 * @param {?} def
 * @return {?}
 */
function applyProviderOverrides(def: NgModuleDefinition) {
    for (let /** @type {?} */ i = 0; i < def.providers.length; i++) {
      const /** @type {?} */ provider = def.providers[i];
      // Make all providers lazy, so that we don't get into trouble
      // with ordering problems of providers on the same element
      provider.flags |= NodeFlags.LazyProvider;
      const /** @type {?} */ override = providerOverrides.get(provider.token);
      if (override) {
        provider.flags = (provider.flags & ~NodeFlags.CatProviderNoDirective) | override.flags;
        provider.deps = splitDepsDsl(override.deps);
        provider.value = override.value;
      }
    }
  }
}
/**
 * @param {?} view
 * @param {?} nodeIndex
 * @param {?} argStyle
 * @param {?=} v0
 * @param {?=} v1
 * @param {?=} v2
 * @param {?=} v3
 * @param {?=} v4
 * @param {?=} v5
 * @param {?=} v6
 * @param {?=} v7
 * @param {?=} v8
 * @param {?=} v9
 * @return {?}
 */
function prodCheckAndUpdateNode(
    view: ViewData, nodeIndex: number, argStyle: ArgumentType, v0?: any, v1?: any, v2?: any,
    v3?: any, v4?: any, v5?: any, v6?: any, v7?: any, v8?: any, v9?: any): any {
  const /** @type {?} */ nodeDef = view.def.nodes[nodeIndex];
  checkAndUpdateNode(view, nodeDef, argStyle, v0, v1, v2, v3, v4, v5, v6, v7, v8, v9);
  return (nodeDef.flags & NodeFlags.CatPureExpression) ?
      asPureExpressionData(view, nodeIndex).value :
      undefined;
}
/**
 * @param {?} view
 * @param {?} nodeIndex
 * @param {?} argStyle
 * @param {?=} v0
 * @param {?=} v1
 * @param {?=} v2
 * @param {?=} v3
 * @param {?=} v4
 * @param {?=} v5
 * @param {?=} v6
 * @param {?=} v7
 * @param {?=} v8
 * @param {?=} v9
 * @return {?}
 */
function prodCheckNoChangesNode(
    view: ViewData, nodeIndex: number, argStyle: ArgumentType, v0?: any, v1?: any, v2?: any,
    v3?: any, v4?: any, v5?: any, v6?: any, v7?: any, v8?: any, v9?: any): any {
  const /** @type {?} */ nodeDef = view.def.nodes[nodeIndex];
  checkNoChangesNode(view, nodeDef, argStyle, v0, v1, v2, v3, v4, v5, v6, v7, v8, v9);
  return (nodeDef.flags & NodeFlags.CatPureExpression) ?
      asPureExpressionData(view, nodeIndex).value :
      undefined;
}
/**
 * @param {?} view
 * @return {?}
 */
function debugCheckAndUpdateView(view: ViewData) {
  return callWithDebugContext(DebugAction.detectChanges, checkAndUpdateView, null, [view]);
}
/**
 * @param {?} view
 * @return {?}
 */
function debugCheckNoChangesView(view: ViewData) {
  return callWithDebugContext(DebugAction.checkNoChanges, checkNoChangesView, null, [view]);
}
/**
 * @param {?} view
 * @return {?}
 */
function debugDestroyView(view: ViewData) {
  return callWithDebugContext(DebugAction.destroy, destroyView, null, [view]);
}
type DebugAction = number;
let DebugAction: any = {};
DebugAction.create = 0;
DebugAction.detectChanges = 1;
DebugAction.checkNoChanges = 2;
DebugAction.destroy = 3;
DebugAction.handleEvent = 4;
DebugAction[DebugAction.create] = "create";
DebugAction[DebugAction.detectChanges] = "detectChanges";
DebugAction[DebugAction.checkNoChanges] = "checkNoChanges";
DebugAction[DebugAction.destroy] = "destroy";
DebugAction[DebugAction.handleEvent] = "handleEvent";


let /** @type {?} */ _currentAction: DebugAction;
let /** @type {?} */ _currentView: ViewData;
let /** @type {?} */ _currentNodeIndex: number|null;
/**
 * @param {?} view
 * @param {?} nodeIndex
 * @return {?}
 */
function debugSetCurrentNode(view: ViewData, nodeIndex: number | null) {
  _currentView = view;
  _currentNodeIndex = nodeIndex;
}
/**
 * @param {?} view
 * @param {?} nodeIndex
 * @param {?} eventName
 * @param {?} event
 * @return {?}
 */
function debugHandleEvent(view: ViewData, nodeIndex: number, eventName: string, event: any) {
  debugSetCurrentNode(view, nodeIndex);
  return callWithDebugContext(
      DebugAction.handleEvent, view.def.handleEvent, null, [view, nodeIndex, eventName, event]);
}
/**
 * @param {?} view
 * @param {?} checkType
 * @return {?}
 */
function debugUpdateDirectives(view: ViewData, checkType: CheckType) {
  if (view.state & ViewState.Destroyed) {
    throw viewDestroyedError(DebugAction[_currentAction]);
  }
  debugSetCurrentNode(view, nextDirectiveWithBinding(view, 0));
  return view.def.updateDirectives(debugCheckDirectivesFn, view);
/**
 * @param {?} view
 * @param {?} nodeIndex
 * @param {?} argStyle
 * @param {...?} values
 * @return {?}
 */
function debugCheckDirectivesFn(
      view: ViewData, nodeIndex: number, argStyle: ArgumentType, ...values: any[]) {
    const /** @type {?} */ nodeDef = view.def.nodes[nodeIndex];
    if (checkType === CheckType.CheckAndUpdate) {
      debugCheckAndUpdateNode(view, nodeDef, argStyle, values);
    } else {
      debugCheckNoChangesNode(view, nodeDef, argStyle, values);
    }
    if (nodeDef.flags & NodeFlags.TypeDirective) {
      debugSetCurrentNode(view, nextDirectiveWithBinding(view, nodeIndex));
    }
    return (nodeDef.flags & NodeFlags.CatPureExpression) ?
        asPureExpressionData(view, nodeDef.index).value :
        undefined;
  }
}
/**
 * @param {?} view
 * @param {?} checkType
 * @return {?}
 */
function debugUpdateRenderer(view: ViewData, checkType: CheckType) {
  if (view.state & ViewState.Destroyed) {
    throw viewDestroyedError(DebugAction[_currentAction]);
  }
  debugSetCurrentNode(view, nextRenderNodeWithBinding(view, 0));
  return view.def.updateRenderer(debugCheckRenderNodeFn, view);
/**
 * @param {?} view
 * @param {?} nodeIndex
 * @param {?} argStyle
 * @param {...?} values
 * @return {?}
 */
function debugCheckRenderNodeFn(
      view: ViewData, nodeIndex: number, argStyle: ArgumentType, ...values: any[]) {
    const /** @type {?} */ nodeDef = view.def.nodes[nodeIndex];
    if (checkType === CheckType.CheckAndUpdate) {
      debugCheckAndUpdateNode(view, nodeDef, argStyle, values);
    } else {
      debugCheckNoChangesNode(view, nodeDef, argStyle, values);
    }
    if (nodeDef.flags & NodeFlags.CatRenderNode) {
      debugSetCurrentNode(view, nextRenderNodeWithBinding(view, nodeIndex));
    }
    return (nodeDef.flags & NodeFlags.CatPureExpression) ?
        asPureExpressionData(view, nodeDef.index).value :
        undefined;
  }
}
/**
 * @param {?} view
 * @param {?} nodeDef
 * @param {?} argStyle
 * @param {?} givenValues
 * @return {?}
 */
function debugCheckAndUpdateNode(
    view: ViewData, nodeDef: NodeDef, argStyle: ArgumentType, givenValues: any[]): void {
  const /** @type {?} */ changed = ( /** @type {?} */((<any>checkAndUpdateNode)))(view, nodeDef, argStyle, ...givenValues);
  if (changed) {
    const /** @type {?} */ values = argStyle === ArgumentType.Dynamic ? givenValues[0] : givenValues;
    if (nodeDef.flags & NodeFlags.TypeDirective) {
      const /** @type {?} */ bindingValues: {[key: string]: string} = {};
      for (let /** @type {?} */ i = 0; i < nodeDef.bindings.length; i++) {
        const /** @type {?} */ binding = nodeDef.bindings[i];
        const /** @type {?} */ value = values[i];
        if (binding.flags & BindingFlags.TypeProperty) {
          bindingValues[normalizeDebugBindingName( /** @type {?} */((binding.nonMinifiedName)))] =
              normalizeDebugBindingValue(value);
        }
      }
      const /** @type {?} */ elDef = /** @type {?} */(( nodeDef.parent));
      const /** @type {?} */ el = asElementData(view, elDef.index).renderElement;
      if (! /** @type {?} */((elDef.element)).name) {
        // a comment.
        view.renderer.setValue(el, `bindings=${JSON.stringify(bindingValues, null, 2)}`);
      } else {
        // a regular element.
        for (let /** @type {?} */ attr in bindingValues) {
          const /** @type {?} */ value = bindingValues[attr];
          if (value != null) {
            view.renderer.setAttribute(el, attr, value);
          } else {
            view.renderer.removeAttribute(el, attr);
          }
        }
      }
    }
  }
}
/**
 * @param {?} view
 * @param {?} nodeDef
 * @param {?} argStyle
 * @param {?} values
 * @return {?}
 */
function debugCheckNoChangesNode(
    view: ViewData, nodeDef: NodeDef, argStyle: ArgumentType, values: any[]): void {
  ( /** @type {?} */((<any>checkNoChangesNode)))(view, nodeDef, argStyle, ...values);
}
/**
 * @param {?} name
 * @return {?}
 */
function normalizeDebugBindingName(name: string) {
  // Attribute names with `$` (eg `x-y$`) are valid per spec, but unsupported by some browsers
  name = camelCaseToDashCase(name.replace(/[$@]/g, '_'));
  return `ng-reflect-${name}`;
}

const /** @type {?} */ CAMEL_CASE_REGEXP = /([A-Z])/g;
/**
 * @param {?} input
 * @return {?}
 */
function camelCaseToDashCase(input: string): string {
  return input.replace(CAMEL_CASE_REGEXP, (...m: any[]) => '-' + m[1].toLowerCase());
}
/**
 * @param {?} value
 * @return {?}
 */
function normalizeDebugBindingValue(value: any): string {
  try {
    // Limit the size of the value as otherwise the DOM just gets polluted.
    return value != null ? value.toString().slice(0, 30) : value;
  } catch ( /** @type {?} */e) {
    return '[ERROR] Exception while trying to serialize the value';
  }
}
/**
 * @param {?} view
 * @param {?} nodeIndex
 * @return {?}
 */
function nextDirectiveWithBinding(view: ViewData, nodeIndex: number): number|null {
  for (let /** @type {?} */ i = nodeIndex; i < view.def.nodes.length; i++) {
    const /** @type {?} */ nodeDef = view.def.nodes[i];
    if (nodeDef.flags & NodeFlags.TypeDirective && nodeDef.bindings && nodeDef.bindings.length) {
      return i;
    }
  }
  return null;
}
/**
 * @param {?} view
 * @param {?} nodeIndex
 * @return {?}
 */
function nextRenderNodeWithBinding(view: ViewData, nodeIndex: number): number|null {
  for (let /** @type {?} */ i = nodeIndex; i < view.def.nodes.length; i++) {
    const /** @type {?} */ nodeDef = view.def.nodes[i];
    if ((nodeDef.flags & NodeFlags.CatRenderNode) && nodeDef.bindings && nodeDef.bindings.length) {
      return i;
    }
  }
  return null;
}
class DebugContext_ implements DebugContext {
private nodeDef: NodeDef;
private elView: ViewData;
private elDef: NodeDef;
/**
 * @param {?} view
 * @param {?} nodeIndex
 */
constructor(public view: ViewData,
public nodeIndex: number|null) {
    if (nodeIndex == null) {
      this.nodeIndex = nodeIndex = 0;
    }
    this.nodeDef = view.def.nodes[nodeIndex];
    let elDef = this.nodeDef;
    let elView = view;
    while (elDef && (elDef.flags & NodeFlags.TypeElement) === 0) {
      elDef = elDef.parent !;
    }
    if (!elDef) {
      while (!elDef && elView) {
        elDef = viewParentEl(elView) !;
        elView = elView.parent !;
      }
    }
    this.elDef = elDef;
    this.elView = elView;
  }
/**
 * @return {?}
 */
private get elOrCompView() {
    // Has to be done lazily as we use the DebugContext also during creation of elements...
    return asElementData(this.elView, this.elDef.index).componentView || this.view;
  }
/**
 * @return {?}
 */
get injector(): Injector { return createInjector(this.elView, this.elDef); }
/**
 * @return {?}
 */
get component(): any { return this.elOrCompView.component; }
/**
 * @return {?}
 */
get context(): any { return this.elOrCompView.context; }
/**
 * @return {?}
 */
get providerTokens(): any[] {
    const /** @type {?} */ tokens: any[] = [];
    if (this.elDef) {
      for (let /** @type {?} */ i = this.elDef.index + 1; i <= this.elDef.index + this.elDef.childCount; i++) {
        const /** @type {?} */ childDef = this.elView.def.nodes[i];
        if (childDef.flags & NodeFlags.CatProvider) {
          tokens.push( /** @type {?} */((childDef.provider)).token);
        }
        i += childDef.childCount;
      }
    }
    return tokens;
  }
/**
 * @return {?}
 */
get references(): {[key: string]: any} {
    const /** @type {?} */ references: {[key: string]: any} = {};
    if (this.elDef) {
      collectReferences(this.elView, this.elDef, references);

      for (let /** @type {?} */ i = this.elDef.index + 1; i <= this.elDef.index + this.elDef.childCount; i++) {
        const /** @type {?} */ childDef = this.elView.def.nodes[i];
        if (childDef.flags & NodeFlags.CatProvider) {
          collectReferences(this.elView, childDef, references);
        }
        i += childDef.childCount;
      }
    }
    return references;
  }
/**
 * @return {?}
 */
get componentRenderElement() {
    const /** @type {?} */ elData = findHostElement(this.elOrCompView);
    return elData ? elData.renderElement : undefined;
  }
/**
 * @return {?}
 */
get renderNode(): any {
    return this.nodeDef.flags & NodeFlags.TypeText ? renderNode(this.view, this.nodeDef) :
                                                     renderNode(this.elView, this.elDef);
  }
/**
 * @param {?} console
 * @param {...?} values
 * @return {?}
 */
logError(console: Console, ...values: any[]) {
    let /** @type {?} */ logViewDef: ViewDefinition;
    let /** @type {?} */ logNodeIndex: number;
    if (this.nodeDef.flags & NodeFlags.TypeText) {
      logViewDef = this.view.def;
      logNodeIndex = this.nodeDef.index;
    } else {
      logViewDef = this.elView.def;
      logNodeIndex = this.elDef.index;
    }
    // Note: we only generate a log function for text and element nodes
    // to make the generated code as small as possible.
    const /** @type {?} */ renderNodeIndex = getRenderNodeIndex(logViewDef, logNodeIndex);
    let /** @type {?} */ currRenderNodeIndex = -1;
    let /** @type {?} */ nodeLogger: NodeLogger = () => {
      currRenderNodeIndex++;
      if (currRenderNodeIndex === renderNodeIndex) {
        return console.error.bind(console, ...values);
      } else {
        return NOOP;
      }
    }; /** @type {?} */((
    logViewDef.factory))(nodeLogger);
    if (currRenderNodeIndex < renderNodeIndex) {
      console.error('Illegal state: the ViewDefinitionFactory did not call the logger!');
      ( /** @type {?} */((<any>console.error)))(...values);
    }
  }
}

function DebugContext__tsickle_Closure_declarations() {
/** @type {?} */
DebugContext_.prototype.nodeDef;
/** @type {?} */
DebugContext_.prototype.elView;
/** @type {?} */
DebugContext_.prototype.elDef;
/** @type {?} */
DebugContext_.prototype.view;
/** @type {?} */
DebugContext_.prototype.nodeIndex;
}

/**
 * @param {?} viewDef
 * @param {?} nodeIndex
 * @return {?}
 */
function getRenderNodeIndex(viewDef: ViewDefinition, nodeIndex: number): number {
  let /** @type {?} */ renderNodeIndex = -1;
  for (let /** @type {?} */ i = 0; i <= nodeIndex; i++) {
    const /** @type {?} */ nodeDef = viewDef.nodes[i];
    if (nodeDef.flags & NodeFlags.CatRenderNode) {
      renderNodeIndex++;
    }
  }
  return renderNodeIndex;
}
/**
 * @param {?} view
 * @return {?}
 */
function findHostElement(view: ViewData): ElementData|null {
  while (view && !isComponentView(view)) {
    view = /** @type {?} */(( view.parent));
  }
  if (view.parent) {
    return asElementData(view.parent, /** @type {?} */(( viewParentEl(view))).index);
  }
  return null;
}
/**
 * @param {?} view
 * @param {?} nodeDef
 * @param {?} references
 * @return {?}
 */
function collectReferences(view: ViewData, nodeDef: NodeDef, references: {[key: string]: any}) {
  for (let /** @type {?} */ refName in nodeDef.references) {
    references[refName] = getQueryValue(view, nodeDef, nodeDef.references[refName]);
  }
}
/**
 * @param {?} action
 * @param {?} fn
 * @param {?} self
 * @param {?} args
 * @return {?}
 */
function callWithDebugContext(action: DebugAction, fn: any, self: any, args: any[]) {
  const /** @type {?} */ oldAction = _currentAction;
  const /** @type {?} */ oldView = _currentView;
  const /** @type {?} */ oldNodeIndex = _currentNodeIndex;
  try {
    _currentAction = action;
    const /** @type {?} */ result = fn.apply(self, args);
    _currentView = oldView;
    _currentNodeIndex = oldNodeIndex;
    _currentAction = oldAction;
    return result;
  } catch ( /** @type {?} */e) {
    if (isViewDebugError(e) || !_currentView) {
      throw e;
    }
    throw viewWrappedDebugError(e, /** @type {?} */(( getCurrentDebugContext())));
  }
}
/**
 * @return {?}
 */
export function getCurrentDebugContext(): DebugContext|null {
  return _currentView ? new DebugContext_(_currentView, _currentNodeIndex) : null;
}
class DebugRendererFactory2 implements RendererFactory2 {
/**
 * @param {?} delegate
 */
constructor(private delegate: RendererFactory2) {}
/**
 * @param {?} element
 * @param {?} renderData
 * @return {?}
 */
createRenderer(element: any, renderData: RendererType2|null): Renderer2 {
    return new DebugRenderer2(this.delegate.createRenderer(element, renderData));
  }
/**
 * @return {?}
 */
begin() {
    if (this.delegate.begin) {
      this.delegate.begin();
    }
  }
/**
 * @return {?}
 */
end() {
    if (this.delegate.end) {
      this.delegate.end();
    }
  }
/**
 * @return {?}
 */
whenRenderingDone(): Promise<any> {
    if (this.delegate.whenRenderingDone) {
      return this.delegate.whenRenderingDone();
    }
    return Promise.resolve(null);
  }
}

function DebugRendererFactory2_tsickle_Closure_declarations() {
/** @type {?} */
DebugRendererFactory2.prototype.delegate;
}

class DebugRenderer2 implements Renderer2 {
/**
 * @param {?} delegate
 */
constructor(private delegate: Renderer2) {}
/**
 * @return {?}
 */
get data() { return this.delegate.data; }
/**
 * @param {?} node
 * @return {?}
 */
destroyNode(node: any) {
    removeDebugNodeFromIndex( /** @type {?} */((getDebugNode(node))));
    if (this.delegate.destroyNode) {
      this.delegate.destroyNode(node);
    }
  }
/**
 * @return {?}
 */
destroy() { this.delegate.destroy(); }
/**
 * @param {?} name
 * @param {?=} namespace
 * @return {?}
 */
createElement(name: string, namespace?: string): any {
    const /** @type {?} */ el = this.delegate.createElement(name, namespace);
    const /** @type {?} */ debugCtx = getCurrentDebugContext();
    if (debugCtx) {
      const /** @type {?} */ debugEl = new DebugElement(el, null, debugCtx);
      debugEl.name = name;
      indexDebugNode(debugEl);
    }
    return el;
  }
/**
 * @param {?} value
 * @return {?}
 */
createComment(value: string): any {
    const /** @type {?} */ comment = this.delegate.createComment(value);
    const /** @type {?} */ debugCtx = getCurrentDebugContext();
    if (debugCtx) {
      indexDebugNode(new DebugNode(comment, null, debugCtx));
    }
    return comment;
  }
/**
 * @param {?} value
 * @return {?}
 */
createText(value: string): any {
    const /** @type {?} */ text = this.delegate.createText(value);
    const /** @type {?} */ debugCtx = getCurrentDebugContext();
    if (debugCtx) {
      indexDebugNode(new DebugNode(text, null, debugCtx));
    }
    return text;
  }
/**
 * @param {?} parent
 * @param {?} newChild
 * @return {?}
 */
appendChild(parent: any, newChild: any): void {
    const /** @type {?} */ debugEl = getDebugNode(parent);
    const /** @type {?} */ debugChildEl = getDebugNode(newChild);
    if (debugEl && debugChildEl && debugEl instanceof DebugElement) {
      debugEl.addChild(debugChildEl);
    }
    this.delegate.appendChild(parent, newChild);
  }
/**
 * @param {?} parent
 * @param {?} newChild
 * @param {?} refChild
 * @return {?}
 */
insertBefore(parent: any, newChild: any, refChild: any): void {
    const /** @type {?} */ debugEl = getDebugNode(parent);
    const /** @type {?} */ debugChildEl = getDebugNode(newChild);
    const /** @type {?} */ debugRefEl = /** @type {?} */(( getDebugNode(refChild)));
    if (debugEl && debugChildEl && debugEl instanceof DebugElement) {
      debugEl.insertBefore(debugRefEl, debugChildEl);
    }

    this.delegate.insertBefore(parent, newChild, refChild);
  }
/**
 * @param {?} parent
 * @param {?} oldChild
 * @return {?}
 */
removeChild(parent: any, oldChild: any): void {
    const /** @type {?} */ debugEl = getDebugNode(parent);
    const /** @type {?} */ debugChildEl = getDebugNode(oldChild);
    if (debugEl && debugChildEl && debugEl instanceof DebugElement) {
      debugEl.removeChild(debugChildEl);
    }
    this.delegate.removeChild(parent, oldChild);
  }
/**
 * @param {?} selectorOrNode
 * @return {?}
 */
selectRootElement(selectorOrNode: string|any): any {
    const /** @type {?} */ el = this.delegate.selectRootElement(selectorOrNode);
    const /** @type {?} */ debugCtx = getCurrentDebugContext();
    if (debugCtx) {
      indexDebugNode(new DebugElement(el, null, debugCtx));
    }
    return el;
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @param {?=} namespace
 * @return {?}
 */
setAttribute(el: any, name: string, value: string, namespace?: string): void {
    const /** @type {?} */ debugEl = getDebugNode(el);
    if (debugEl && debugEl instanceof DebugElement) {
      const /** @type {?} */ fullName = namespace ? namespace + ':' + name : name;
      debugEl.attributes[fullName] = value;
    }
    this.delegate.setAttribute(el, name, value, namespace);
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?=} namespace
 * @return {?}
 */
removeAttribute(el: any, name: string, namespace?: string): void {
    const /** @type {?} */ debugEl = getDebugNode(el);
    if (debugEl && debugEl instanceof DebugElement) {
      const /** @type {?} */ fullName = namespace ? namespace + ':' + name : name;
      debugEl.attributes[fullName] = null;
    }
    this.delegate.removeAttribute(el, name, namespace);
  }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
addClass(el: any, name: string): void {
    const /** @type {?} */ debugEl = getDebugNode(el);
    if (debugEl && debugEl instanceof DebugElement) {
      debugEl.classes[name] = true;
    }
    this.delegate.addClass(el, name);
  }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
removeClass(el: any, name: string): void {
    const /** @type {?} */ debugEl = getDebugNode(el);
    if (debugEl && debugEl instanceof DebugElement) {
      debugEl.classes[name] = false;
    }
    this.delegate.removeClass(el, name);
  }
/**
 * @param {?} el
 * @param {?} style
 * @param {?} value
 * @param {?} flags
 * @return {?}
 */
setStyle(el: any, style: string, value: any, flags: RendererStyleFlags2): void {
    const /** @type {?} */ debugEl = getDebugNode(el);
    if (debugEl && debugEl instanceof DebugElement) {
      debugEl.styles[style] = value;
    }
    this.delegate.setStyle(el, style, value, flags);
  }
/**
 * @param {?} el
 * @param {?} style
 * @param {?} flags
 * @return {?}
 */
removeStyle(el: any, style: string, flags: RendererStyleFlags2): void {
    const /** @type {?} */ debugEl = getDebugNode(el);
    if (debugEl && debugEl instanceof DebugElement) {
      debugEl.styles[style] = null;
    }
    this.delegate.removeStyle(el, style, flags);
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setProperty(el: any, name: string, value: any): void {
    const /** @type {?} */ debugEl = getDebugNode(el);
    if (debugEl && debugEl instanceof DebugElement) {
      debugEl.properties[name] = value;
    }
    this.delegate.setProperty(el, name, value);
  }
/**
 * @param {?} target
 * @param {?} eventName
 * @param {?} callback
 * @return {?}
 */
listen(
      target: 'document'|'windows'|'body'|any, eventName: string,
      callback: (event: any) => boolean): () => void {
    if (typeof target !== 'string') {
      const /** @type {?} */ debugEl = getDebugNode(target);
      if (debugEl) {
        debugEl.listeners.push(new EventListener(eventName, callback));
      }
    }

    return this.delegate.listen(target, eventName, callback);
  }
/**
 * @param {?} node
 * @return {?}
 */
parentNode(node: any): any { return this.delegate.parentNode(node); }
/**
 * @param {?} node
 * @return {?}
 */
nextSibling(node: any): any { return this.delegate.nextSibling(node); }
/**
 * @param {?} node
 * @param {?} value
 * @return {?}
 */
setValue(node: any, value: string): void { return this.delegate.setValue(node, value); }
}

function DebugRenderer2_tsickle_Closure_declarations() {
/** @type {?} */
DebugRenderer2.prototype.delegate;
}




// WEBPACK FOOTER //
// ../../../../packages/core/src/view/services.ts